import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { TelemetryOutlier } from "@busright/data-contract";

import { DataTable } from "@/components/DataTable";

// Per-device telemetry outliers (real latest readings, NOT fleet averages). Rows drill to the
// Device-360 view at /device/:serial. Devices with no serial are not linkable, so they render but
// don't navigate.

export interface TelOutliersProps {
  readonly rows: readonly TelemetryOutlier[];
  // How to render the metric value (e.g. `${v}°C`).
  readonly formatValue: (v: number) => string;
  readonly valueHeader: string;
  readonly emptyTitle: string;
}

const HOURS_PER_DAY = 24;
// Below this many hours, show the age in hours; otherwise in days.
const AGE_DAYS_CUTOFF_HOURS = 48;

const formatAge = (hours: number | null): string => {
  if (hours === null) {
    return "—";
  }
  if (hours < AGE_DAYS_CUTOFF_HOURS) {
    return `${Math.round(hours)}h`;
  }
  return `${Math.round(hours / HOURS_PER_DAY)}d`;
};

export const TelOutliers = ({
  rows,
  formatValue,
  valueHeader,
  emptyTitle,
}: TelOutliersProps): ReactNode => {
  const navigate = useNavigate();

  return (
    <DataTable
      rows={rows}
      rowKey={(r) => r.esperDeviceId}
      emptyTitle={emptyTitle}
      onRowClick={(r) => {
        if (r.serial) {
          void navigate(`/device/${r.serial}`);
        }
      }}
      columns={[
        {
          key: "serial",
          header: "Serial",
          render: (r) => <span className="mono">{r.serial ?? "—"}</span>,
        },
        {
          key: "hwModel",
          header: "Model",
          render: (r) => r.hwModel ?? "—",
        },
        {
          key: "value",
          header: valueHeader,
          align: "right",
          render: (r) => <span className="br-tabular-nums">{formatValue(r.value)}</span>,
        },
        {
          key: "age",
          header: "Reading age",
          align: "right",
          render: (r) => <span className="br-tabular-nums">{formatAge(r.readingAgeHours)}</span>,
        },
      ]}
    />
  );
};
